import { useState } from 'react';
import { Button } from '@/components/ui';
import { useAuthStore } from '@/stores/authStore';
import type { MilestoneView, ProjectView } from '@/types/current-project';
import { taskSchema } from '@/types/current-project';
import type { useProjectCommand } from '@/hooks/useProjectCommand';
import { TaskEditor, NumberField } from './TaskEditor';
import { projectDateLabel } from '@/lib/project-presentation';

export function MilestoneTasks({
  milestone,
  project,
  command,
  editable = false,
}: {
  milestone: MilestoneView;
  project?: ProjectView;
  command?: ReturnType<typeof useProjectCommand>;
  editable?: boolean;
}) {
  const { tasks, taskStorageId } = milestone.taskStorage;
  const account = useAuthStore((s) => s.session?.accountId);
  const active =
    editable &&
    project &&
    command &&
    !project.cancelled &&
    !project.completed &&
    !project.activeDisputeId &&
    !milestone.frozen &&
    milestone.status !== 'Completed';
  const coordinator = active && account === project.coordinator;
  const progressAllowed = [
    'InProgress',
    'CompletionRequested',
    'ChangesRequested',
  ].includes(milestone.status ?? '');
  return (
    <details className="rounded-[--radi-6] border border-input bg-background p-4">
      <summary className="cursor-pointer font-semibold">
        {tasks.length} attached {tasks.length === 1 ? 'task' : 'tasks'}
      </summary>
      {active && !progressAllowed && (
        <p className="mt-3 text-sm text-muted-foreground">
          Task preparation is available. Progress can be recorded once this
          milestone starts.
        </p>
      )}
      {!tasks.length && <p>No tasks yet. Tracking tasks are optional.</p>}
      {tasks.map(({ taskId, task, updatedAt }) => {
        const due = task.dueAt == null ? null : new Date(task.dueAt * 1000);
        return (
          <article
            key={taskId}
            className="mt-4 space-y-2 border-t border-input pt-4"
          >
            {updatedAt != null && (
              <p className="text-xs text-muted-foreground">
                Updated {projectDateLabel(updatedAt)}
              </p>
            )}
            <h5 className="font-semibold">
              #{taskId} {task.title}
            </h5>
            <p className="whitespace-pre-wrap break-words">
              {task.description}
            </p>
            <p className="text-sm">
              {task.status} · {task.taskType} · {task.priority}
            </p>
            {coordinator && (
              <TaskEditor
                projectId={project.projectId}
                storageId={taskStorageId}
                taskId={taskId}
                task={task}
                command={command}
                disabled={false}
                operational
                progressAllowed={progressAllowed}
              />
            )}
            {active &&
              progressAllowed &&
              account !== project.client &&
              account !== project.coordinator &&
              task.assignees.includes(account ?? '') && (
                <TaskProgress
                  key={JSON.stringify(task)}
                  projectId={project.projectId}
                  storageId={taskStorageId}
                  taskId={taskId}
                  task={task}
                  command={command}
                />
              )}
            <p className="text-sm">
              Estimated {task.estimatedMinutes} minutes · Logged{' '}
              {task.loggedMinutes} minutes
            </p>
            <p className="text-sm">
              {task.assignees.length
                ? `${task.assignees.length} ${task.assignees.length === 1 ? 'assignee' : 'assignees'}`
                : 'Unassigned'}
            </p>
            <p className="text-sm">
              Due:{' '}
              {due
                ? Number.isNaN(due.getTime())
                  ? `${task.dueAt} Unix seconds`
                  : due.toISOString()
                : 'Not set'}
            </p>
          </article>
        );
      })}
      {coordinator && (
        <div className="mt-4">
          <TaskEditor
            projectId={project.projectId}
            storageId={taskStorageId}
            command={command}
            disabled={false}
            operational
            progressAllowed={progressAllowed}
          />
        </div>
      )}
    </details>
  );
}

function TaskProgress({
  projectId,
  storageId,
  taskId,
  task,
  command,
}: {
  projectId: string;
  storageId: string;
  taskId: number;
  task: MilestoneView['taskStorage']['tasks'][number]['task'];
  command: ReturnType<typeof useProjectCommand>;
}) {
  const [status, setStatus] = useState(task.status);
  const [minutes, setMinutes] = useState(task.loggedMinutes);
  return (
    <form
      className="backendForm space-y-3"
      aria-label={'Progress for task #' + taskId}
      onSubmit={(e) => {
        e.preventDefault();
        void command.run({
          kind: 'task-progress',
          projectId,
          storageId,
          taskId,
          payload: { status, loggedMinutes: minutes },
        });
      }}
    >
      <fieldset
        disabled={command.busy || command.pending || command.blocked}
        className="space-y-3"
      >
        <label>
          Status
          <select
            aria-label="Status"
            className="input bg-background text-foreground [color-scheme:dark]"
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
          >
            {taskSchema.shape.task.shape.status.options.map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </label>
        <NumberField
          label="Logged minutes"
          value={minutes}
          onChange={setMinutes}
        />
        <Button type="submit">Save progress</Button>
      </fieldset>
    </form>
  );
}
